<script lang="ts" setup>
import Modal from '@/core/components/modals/Modal.vue';
import ContractTemplateWizardBasicData from './ContractTemplateWizardBasicData.vue';
import ContractTemplateWizardDetailData from './ContractTemplateWizardDetailData.vue';
import ContractTemplateWizardPerformanceData from './ContractTemplateWizardPerformanceData.vue';

import {
   ContractTemplateFieldName,
   ContractTemplateFieldNameKey,
} from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import {
   ContractTemplate,
   ContractTemplateDTO,
   ContractTemplateField,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import {
   mapContractTemplateWizardErrors,
   useContractTemplateWizardSchema,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template-wizard.schema';
import store from '@/core/store';
import { useChangeDetection, useYupValidation } from '@nxtpeople/nxt-styles';
import { useContractTemplateKeys } from './useContractTemplateKeys';

//#region Globals
const { t } = useI18n();
const { dispatch, state } = useStore();
const customer = inject('customer', ref<Customer>());

const contractTemplate = ref<ContractTemplateDTO>({
   name: '',
   contract_template_fields: [],
   customer_id: customer.value?.id,
   customer: customer.value,
   team_id: state.userInfo?.userInfo?.default_team_id,
   team: state.userInfo?.userInfo?.default_team,
});
const isInitializing = ref(true);
provide('contractTemplate', contractTemplate);
const { ALL_KEYS } = useContractTemplateKeys();
//#endregion

//#region Props
const emits = defineEmits<{ (e: 'onCancel'): void; (e: 'onSave', contractTemplate: ContractTemplateDTO): void }>();
const props = defineProps({
   loading: { type: Boolean, default: false },
   initialContractTemplate: { type: Object as PropType<ContractTemplate>, default: null },
});
const { loading, initialContractTemplate } = toRefs(props);
const title = computed(() => {
   if (initialContractTemplate.value) {
      return t('generic.feedback.change-item', { name: t('customers.contract-templates.contract-template') });
   }
   return t('generic.feedback.create-item', { name: t('customers.contract-templates.contract-template') });
});
//#endregion

//#region Field info
const loadingContractTemplateFieldNames = ref(true);
const contractTemplateFieldNames = ref<Array<ContractTemplateFieldName>>([]);
const { getSchemaForTab } = useContractTemplateWizardSchema(() => contractTemplateFieldNames.value);
const { errors, validate, autoValidate } = useYupValidation({
   schema: getSchemaForTab('basic-data'),
   data: contractTemplate,
});
const { hasChanges, resetChangeDetection } = useChangeDetection(contractTemplate);
const displayErrors = computed(() =>
   mapContractTemplateWizardErrors(errors.value, contractTemplate.value, contractTemplateFieldNames.value),
);

onMounted(async () => {
   try {
      loadingContractTemplateFieldNames.value = true;
      const company = await dispatch('company/getCompany', {
         companyId: customer.value?.company_id,
         query: { embed: 'city,invoiceGroups.city,costCenters,shifts,fulltimes,departments,workstations' },
      });
      customer.value!.company = {
         ...customer.value!.company,
         ...company,
      };
      await nextTick();
      contractTemplateFieldNames.value = await dispatch('contractTemplateFieldName/getContractTemplateFieldNames').then(
         mapContractTemplateFieldNames,
      );

      if (props.initialContractTemplate) setInitialContractTemplate();
   } catch (ex) {
      emits('onCancel');
   } finally {
      loadingContractTemplateFieldNames.value = false;
   }
});

function setInitialContractTemplate() {
   //map contract template fields to include key & name
   const contract_template_fields = [...props.initialContractTemplate.contract_template_fields].map((ctf) => {
      const fieldName = contractTemplateFieldNames.value.find((ctfn) => ctfn.id === ctf.contract_template_field_name?.id);
      if (!fieldName) return { ...ctf };
      return {
         ...ctf,
         field_key: fieldName.key,
         contract_template_field_name_id: fieldName.id,
         is_required: fieldName.is_required ? true : !!ctf.is_required,
      };
   });

   //Copy object without reference
   contractTemplate.value = JSON.parse(
      JSON.stringify({ ...props.initialContractTemplate, customer: customer.value, contract_template_fields }),
   );
}

function mapContractTemplateFieldNames(fieldNames: Array<ContractTemplateFieldName>) {
   return fieldNames.map((fieldName) => {
      const fieldKey = ALL_KEYS.value.find((fk) => fk.key === fieldName.key);
      if (!fieldKey) return fieldName;
      return {
         ...fieldName,
         label: fieldKey?.translation,
         display_key: fieldKey?.display_key || 'name',
         max_length: fieldKey?.max_length,
         options: fieldKey?.options,
         optionsUrl: fieldKey?.optionsUrl,
         customDisplayTemplate: fieldKey?.customDisplayTemplate,
         dropdownParams: fieldKey?.dropdownParams,
      };
   });
}

const salaryTypeFieldName = computed(() =>
   contractTemplateFieldNames.value.find((ctfn) => ctfn.key === ContractTemplateFieldNameKey.SalaryTypeId),
);
const salaryTypeField = computed(() =>
   contractTemplate.value.contract_template_fields.find((ctf) => ctf.field_key === ContractTemplateFieldNameKey.SalaryTypeId),
);

function updateSalaryTypeField(field: ContractTemplateField) {
   const index = contractTemplate.value.contract_template_fields.findIndex(
      (ctf) => ctf.field_key === ContractTemplateFieldNameKey.SalaryTypeId,
   );

   if (index > -1) return contractTemplate.value.contract_template_fields.splice(index, 1, field);

   return contractTemplate.value.contract_template_fields.push({
      ...field,
      contract_template_field_name: salaryTypeFieldName.value,
      contract_template_field_name_id: salaryTypeFieldName.value!.id,
      is_fixed: false,
      is_required: salaryTypeFieldName.value!.is_required || false,
      field_key: salaryTypeFieldName.value!.key,
   });
}

provide('salaryTypeFieldName', salaryTypeFieldName);
provide('salaryTypeField', salaryTypeField);
provide('updateSalaryTypeField', updateSalaryTypeField);

//#endregion

//#region Tabs
const tabs = computed(() => [
   { key: 'basic-data', title: t('contracts.basic-data') },
   { key: 'detail-data', title: t('contracts.detail-data') },
   { key: 'performance-data', title: t('contracts.performance-data') },
]);
const { activeTabKey, activeTabIndex, isFirstTab, isLastTab, nextTab, previousTab } = useTabs(tabs);
//#endregion

//#region Update Contract Template
function updateField(payload: { fieldNameId: number; key: string; value: any }) {
   const { fieldNameId, key, value } = payload;
   if (!fieldNameId) return;

   const fields = contractTemplate.value.contract_template_fields;
   const index = fields.findIndex((ctf) => ctf.contract_template_field_name_id === fieldNameId);
   const fieldName = contractTemplateFieldNames.value.find((fn) => fn.id === fieldNameId);
   const fieldKey = fieldName?.key;
   const isFieldRequired = !!fieldName?.is_required;

   if (index > -1) {
      return fields.splice(index, 1, {
         ...fields[index],
         [key]: value,
         is_required: isFieldRequired ? true : !!fields[index].is_required,
      });
   }

   return fields.push({
      contract_template_field_name_id: fieldNameId,
      is_fixed: false,
      is_required: isFieldRequired,
      field_key: fieldKey,
      calculated_values: [],
      [key]: value,
   });
}

//#endregion

//#region Save Template Wizard

async function onBasicDataInitialized() {
   await nextTick();
   await nextTick();
   resetChangeDetection();
   isInitializing.value = false;
}

async function saveContractTemplate() {
   if (props.initialContractTemplate || isLastTab.value) {
      for (const tab of tabs.value) {
         const { isInvalid } = validate(getSchemaForTab(tab.key));
         if (isInvalid) {
            activeTabKey.value = tab.key;
            autoValidate.value = true;
            return;
         }
      }
   } else {
      const currentTab = tabs.value[activeTabIndex.value];
      const { isInvalid } = validate(getSchemaForTab(currentTab.key));
      if (isInvalid) {
         autoValidate.value = true;
         return;
      }

      return nextTab();
   }

   const requiredFieldNameIds = new Set(
      contractTemplateFieldNames.value.filter((fieldName) => !!fieldName.is_required).map((fieldName) => fieldName.id),
   );

   const contract_template_fields = contractTemplate.value.contract_template_fields
      .filter((ctf) => ctf.value !== null)
      .map((field) => {
         const ctf = {
            ...field,
            is_required: requiredFieldNameIds.has(field.contract_template_field_name_id) ? true : !!field.is_required,
         };

         if (ctf.field_key === ContractTemplateFieldNameKey.Salary) {
            ctf.value = fixedNumber(ctf.value, 4);
         }

         if ((ctf.calculated_values || []).length) {
            return {
               ...ctf,
               calculated_values: (ctf.calculated_values || []).map((cv) => {
                  return {
                     ...cv,
                     value: ctf.field_key === ContractTemplateFieldNameKey.Salary ? fixedNumber(cv.value, 4) : cv.value,
                     conditions: cv.conditions.map((c) => {
                        const calculated_field_condition_type_id =
                           typeof c.calculated_field_condition_type_id === 'string'
                              ? c.calculated_field_condition_type_id.split('-')[0]
                              : c.calculated_field_condition_type_id;

                        return { ...c, calculated_field_condition_type_id };
                     }),
                  };
               }),
            };
         }
         return ctf;
      });

   return emits('onSave', { ...contractTemplate.value, contract_template_fields });
}
//#endregion

//#region Dropdowns
onMounted(() => {
   dispatch('workerStatute/getWorkerStatutes');
   dispatch('workerType/getWorkerTypes');
   dispatch('salaryType/getSalaryTypes');
   dispatch('salaryInclude/getSalaryIncludes');
   dispatch('recruitmentType/getRecruitmentTypes');
   dispatch('replacementMotive/getTerminationReasons');
   dispatch('replacementMotive/getSuspensionReasons');
   dispatch('shiftType/getShiftTypes');
});
//#endregion

//#region disabled
const permissions = computed(() => store.state.permission?.permissions?.customers);
const disabled = computed(() => {
   if (!props.initialContractTemplate) return props.loading;
   if (!checkAccess('update', permissions.value)) return true;
   return props.loading;
});
//#endregion
</script>

<template>
   <Modal
      v-model:active-tab-key="activeTabKey"
      :title="title"
      :class="'contract-template-wizard'"
      :primary-action-text="props.initialContractTemplate ? t('actions.change') : isLastTab ? t('actions.add') : t('generic.form.next')"
      :secondary-action-text="t('generic.form.previous')"
      :show-prevent-modal="!isInitializing && hasChanges"
      :tabs="tabs"
      :height="'90vh'"
      :width="'75vw'"
      :has-secondary-action="!isFirstTab && !initialContractTemplate"
      :loading="loading || loadingContractTemplateFieldNames"
      :disabled="disabled"
      :disable-tabs="!initialContractTemplate"
      :has-primary-action="!disabled"
      @on-primary-action="saveContractTemplate"
      @on-secondary-action="previousTab"
      @on-cancel="emits('onCancel')"
   >
      <div v-if="loadingContractTemplateFieldNames" class="contract-template-wizard__loading">
         <NxtSpinner :size="'giant'"></NxtSpinner>
      </div>

      <template v-if="!loadingContractTemplateFieldNames" #basic-data="{ key }">
         <ContractTemplateWizardBasicData
            :id="key"
            :fields="contractTemplate.contract_template_fields"
            :field-names="contractTemplateFieldNames"
            :errors="displayErrors"
            :loading="disabled"
            @on-update-field="updateField"
            @on-initialized="onBasicDataInitialized"
         />
      </template>
      <template v-if="!loadingContractTemplateFieldNames" #detail-data="{ key }">
         <ContractTemplateWizardDetailData
            :id="key"
            :fields="contractTemplate.contract_template_fields"
            :field-names="contractTemplateFieldNames"
            :errors="displayErrors"
            :loading="disabled"
            @on-update-field="updateField"
         />
      </template>
      <template v-if="!loadingContractTemplateFieldNames" #performance-data="{ key }">
         <ContractTemplateWizardPerformanceData
            :id="key"
            :fields="contractTemplate.contract_template_fields"
            :field-names="contractTemplateFieldNames"
            :errors="displayErrors"
            :loading="disabled"
            @on-update-field="updateField"
         />
      </template>
   </Modal>
</template>

<style lang="scss">
.contract-template-wizard__loading {
   height: 100%;
   width: 100%;
   display: flex;
   align-items: center;
   justify-content: center;
}
</style>
